import React from 'react';
import './App.css';
import 'reactjs-popup/dist/index.css';

import { CssBaseline, Button, AppBar, MenuList, MenuItem, Tooltip, Snackbar, IconButton } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { withStyles } from '@mui/styles';

import { Close, Warning, ArrowDropDown, ArrowDropUp, Edit, Replay, FileCopy } from '@mui/icons-material';

import {stringify as stringifyParams} from 'query-string';
import * as FlexLayout from 'flexlayout-react';

import TokenControl from './TokenControl';
import RequestControl from './RequestControl';
import DetailsItemList from './DetailsItemList';
import DetailsTabs from './DetailsTabs';
import defaultLayout from './defaultLayout';
import RequestRawInput from './RequestRawInput';
import { muiTheme, muiStyles} from './mui';
import { copyToClipboard, copyToClipboardEnabled, renderItem, simpleTime, verifyHost } from './utils';
import Popup from 'reactjs-popup';
import {FixClient} from './fix/FixClient';
import FixLoginControl from './fix/FixLoginControl';
import FixRequestRawInput from './fix/FixRequestRawInput';
import defaultLayoutFix from './defaultLayoutFix';
import {SERVICE_NAMES} from './constants';
import { safeJsonParse } from './utils';

// eslint-disable-next-line no-undef
const env = process.env;

// TODO default should be platformHost?
const defaultServerUrl = env.REACT_APP_DEFAULT_SERVER_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8082' : 'https://test.deribit.com');

const apiJson = '/static/deribit_api_v2.json';
const historyApiJson = '/api/v2/api_spec.json';
const fixApiPath = '/static/FIX44.xml';
const bustFixApiCache = true;
const fixApiXml = bustFixApiCache ? `${fixApiPath}?${Date.now()}` : fixApiPath;

const EVENT_NODES_ALLOWED_METHODS = [
	'/public/subscribe',
	'/public/unsubscribe',
	'/public/unsubscribe_all',
	'/public/test',
	'/public/set_heartbeat',
	'/public/hello'
];

const HISTORY_ALLOWED_METHODS = [
	'/public/get_instrument',
	'/public/get_instruments',
	'/public/get_last_trades_by_currency',
	'/public/get_last_trades_by_currency_and_time',
	'/public/get_last_trades_by_instrument',
	'/public/get_last_trades_by_instrument_and_time',
	'/public/get_tradingview_chart_data',
	'/private/get_user_trades_by_instrument_and_time',
	'/private/get_user_trades_by_instrument',
	'/private/get_user_trades_by_currency_and_time',
	'/private/get_user_trades_by_currency',
];

const SERVER_URL = 'serverUrl';
const LAYOUT = 'layout';
const LAYOUT_FIX = 'layout_fix';

const WS_CLOSED = 0;
const WS_CONNECTING = 1;
const WS_OPENED = 2;

const HEARTBEAT_TIMEOUT = 60 * 1000;
const WS_REOPEN_TIMEOUT = 30 * 1000;
const TOKEN_EXPIRY_DEV = 3600 * 24 * 30; // 30 days

const allowCopyInDetailsList = copyToClipboardEnabled();
const iconCell = ['24px', false, false, false];

function toQsParams(params) {
	return stringifyParams(params, {arrayFormat: 'bracket'});
}

function getPlatformHost() {
	return env.REACT_APP_DEFAULT_SERVER_URL || `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
}

function isProperOriginOfPostMessage(origin) {
	if (origin === getPlatformHost()) {
		return true;
	}

	let originUrl = new URL(origin);
	let originHostname = originUrl.hostname;
	let locationHostname = window.location.hostname;
	let locationProtocol = window.location.protocol;
	let originProtocol = originUrl.protocol;

	if (originHostname === 'localhost') {
		return true;
	}

	if (originHostname.endsWith('.deribit.com') && locationHostname.endsWith('.deribit.com')) {
		return locationProtocol === originProtocol && locationHostname.includes('test.') === originHostname.includes('test.');
	}

	return false;
}

function getScopeString(serverUrl) {
    const sessionScopeString = 'session:apiconsole-' + Math.random().toString(36).substring(2, 15);
    const isDevEnv = serverUrl.includes('localhost') || serverUrl.includes('test.deribit.com');

    if (isDevEnv) {
        const expiryString = `expires:${TOKEN_EXPIRY_DEV}`;
        return `${sessionScopeString} ${expiryString}`;
    }

    return sessionScopeString;
}

function pathTo(file) {
	let path = window.location.pathname
		.replace(/index.html$/, '')
		.replace(/fix/, '');

	return path + (path.endsWith('/') ? '' : '/') + file;
}

class App extends React.Component {
	constructor(props) {
		super(props);

		const isFix = false;
        const cachedLayout = localStorage.getItem(LAYOUT);

		const cachedUrl = localStorage.getItem(SERVER_URL);
		const hostUrl = cachedUrl || getPlatformHost();

		this.state = {
			requestID: 0,

			ws: null,
			wsState: WS_CLOSED,

			token: {},
			serverUrl: hostUrl,
			fixApiPath: hostUrl + fixApiXml,
			model: FlexLayout.Model.fromJson(cachedLayout ? JSON.parse(cachedLayout) : this.getDefaultLayout(isFix)),

			isFix: isFix,

			snackbarOpen: false,

			testnet: window.location.hostname.endsWith('test.deribit.com'),
		};

		this.requestsList = null;
		this.notificationsList = null;
		this.details = null;

		this.requestSummary = this.requestSummary.bind(this);
		this.notificationSummary = this.notificationSummary.bind(this);

		this.accessTokenChanged = this.accessTokenChanged.bind(this);
		this.refreshTokenChanged = this.refreshTokenChanged.bind(this);

		this.getToken = this.getToken.bind(this);
		this.refreshToken = this.refreshToken.bind(this);
		this.invalidateToken = this.invalidateToken.bind(this);
		this.isAuthenticated = this.isAuthenticated.bind(this);

		this.sendRequest = this.sendRequest.bind(this);
		this.unsubAll = this.unsubAll.bind(this);

		this.wsRequestInfo = {};
		this.getTokenRequestID = undefined;

		this.fixClient = new FixClient({
			serverUrl: this.getFixUrl(hostUrl),
			onMessage: (message) => this.handleFixMessage(message),
			onConnect: () => this.onFixConnected(),
			onDisconnect: (message) => this.onFixDisconnected(message),
			onError: (error) => this.onFixError(error),
		});

		window.onbeforeunload = () => {
			this.fixClient.disconnect();
			this.closeWs();
		};
	}

	async getServices(isFix = false) {
		const servicesUrl = `${getPlatformHost()}/api/v2/public/services`;
		const { result } = await fetch(servicesUrl).then(resp => resp.json());

		if (!result || !result.main) {
			throw new Error(`[App - getServices()] No "main" service found at "${servicesUrl}"`);
		}

		if (!result.history) {
			console.error(`[App - getServices()] No "history" service found at "${servicesUrl}"`);
			result.history = {};
		}

		if (!result.public) {
			console.error(`[App - getServices()] No "public" service found at "${servicesUrl}"`);
			result.public = {};
		}

		// history
		result.history.apiPath = result.history.restEndpoint.replace('/api/v2', historyApiJson);
		result.history.allowedMethods = HISTORY_ALLOWED_METHODS;

		// main (platform)
		result.main.apiPath = result.main.restEndpoint.replace('/api/v2', apiJson);

		// public (event nodes)
		result.public.apiPath = result.main.apiPath;
		result.public.allowedMethods = EVENT_NODES_ALLOWED_METHODS;

		Object.entries(result).forEach(([serviceName, serviceData]) => serviceData.name = SERVICE_NAMES[serviceName]);

		const { main, ...rest } = result;

        return { services: { main, ...rest }, selectedService: isFix ? undefined : result.main };
	}

	componentDidMount() {
        (async () => {
            const services = await this.getServices();
            this.setState(services, this._componentDidMount);
        })();
    }

    _componentDidMount() {
		if (window.opener) {
			let openerTimeout;
			window.onmessage = (event) => {
				if(!isProperOriginOfPostMessage(event.origin)) {
					console.log(`unknown origin: ${event.origin}`);
					return;
				}

				let message = event.data;
				if (message.message === 'client_credentials') {
					if(openerTimeout) {
						clearTimeout(openerTimeout);
					}

					let clientId = message.params.client_id,
						clientSecret = message.params.client_secret,
						tokenState = { grant_type: 'client_credentials', client_id: clientId, client_secret: clientSecret };
					if(!this.tokenControl) {
						this.onTokenControlReady = (tokenManager) => {
							tokenManager.setState(tokenState);
							this.onTokenControlReady = null;
						};
					} else {
						this.tokenControl.setState(tokenState);
					}
					this.setServerUrl(window.location.origin, () => this.getToken('client_credentials', clientId, clientSecret));
				}
			};
			let url = new URL(window.location.href);
			let keyId = url.searchParams.get('key_id') || undefined;
			if(keyId) {
				window.opener.postMessage({ message: 'get_client_credentials', key_id: keyId }, '*');

				openerTimeout = setTimeout(() => { // in case the message won't arrive
					openerTimeout = null;
					this.tryConnect();
				}, 200);
				return;
			}
		}
		this.tryConnect();
	}

	tryConnect() {
		if (this.state.isFix) {
			return;
		}

		verifyHost(this.state.serverUrl, () => {
			this.openWs(this.getWsUrl());
		}, () => {
			verifyHost(defaultServerUrl, () => {
				this.setServerUrl(defaultServerUrl);
			});
		});
	}

	setServerUrl(newServerUrl, onWsOpen = null) {
		newServerUrl = newServerUrl.replace(/\/+$/, '');

		if (!this.state.isFix) {
			this.closeWs();
			this.openWs(this.getWsUrl(newServerUrl), onWsOpen);
		}

		if (newServerUrl !== this.state.serverUrl) {
			localStorage.setItem(SERVER_URL, newServerUrl);
			this.setState({serverUrl: newServerUrl, token: {}});
			if (this.requestControl) {
				this.requestControl.fetchInstruments();
				this.requestControl.setServerUrl(newServerUrl);
			}

			this.fixClient.setServerUrl(this.getFixUrl(newServerUrl));
		}
	}

	getRestUrl() {
		return this.state.selectedService?.restEndpoint;
	}

	getWsUrl(url) {
		if(url) {
			return url.slice().replace(/^http/, 'ws') + '/ws/api/v2';
		}
		return this.state.services['main']?.wsEndpoint;
	}

	getFixUrl(url) {
		if (url) {
			return url.slice().replace(/^http/, 'ws') + '/fix/ws';
		}
		return this.state.serverUrl.slice().replace(/^http/, 'ws') + '/fix/ws';
	}

	showDetails(item) {
		if(this.details) {
			this.details.setItem(item);
		}
	}

	openWs(wsUrl, onOpen) {
		if (this.state.isFix) {
			return;
		}

		this.closeWs();

		if(!wsUrl) {
			wsUrl = this.getWsUrl();
		}
		let ws = new WebSocket(wsUrl);
		this.setState({ ws: ws, wsState: WS_CONNECTING });
		let heartbeatInterval;
		ws.onopen = () => {
			heartbeatInterval = setInterval(() => {
				ws.send('PING');
			}, HEARTBEAT_TIMEOUT);
			this.setState({ wsState: WS_OPENED, snackbarOpen: false });
			if (onOpen) onOpen();
		};
		ws.onclose = () => {
			clearInterval(heartbeatInterval);
			this.wsOpenTimeout = setTimeout(() => {
				this.openWs(wsUrl, onOpen);
				this.wsOpenTimeout = null;
			}, WS_REOPEN_TIMEOUT);
			this.setState({ wsState: WS_CLOSED, snackbarOpen: true });
		};
		ws.onmessage = (event) => {
			if(event.data === 'PONG') {
				return;
			}
			let message = safeJsonParse(event.data);
			if (message.method) { // subscribtion and heartbeat notifications contain "method" field
				this.addNotification(message);
			} else {
				if (message.result !== undefined && message.id === this.getTokenRequestID) {
					if (this.requestControl) {
						this.requestControl.setTokenExpirationTimeout(message.result.expires_in || 900);
					}
					this.authScope = message.result.scope;
					this.setState({ token: message.result });
				}
				if(this.wsRequestInfo[message.id] !== undefined) {
					let {method, extra} = this.wsRequestInfo[message.id];
					this.addRequest(message, method, 'ws', false, extra);
					delete this.wsRequestInfo[message.id];
				}
			}
		};
	}

	closeWs() {
		if(this.wsOpenTimeout) {
			clearTimeout(this.wsOpenTimeout);
			this.wsOpenTimeout = null;
		}
		let ws = this.state.ws;
		if(ws && this.state.wsState) {
			ws.onclose = () => {};
			ws.close();
			this.setState({ws: null, wsState: WS_CLOSED});
		}
	}

	addRequest(message, method, type, isRequest=false, extra=undefined) {
		let item = {date: new Date(), type: type, isRequest: isRequest, message: message, method: method, extra: extra};

		if (this.requestsList) {
			this.requestsList.addItem(item, !isRequest);
		}
	}

	addFixRequest(request) {
		const detailedMessage = this.fixClient.getDetailedMessage(request);
		if (!detailedMessage) {
			return;
		}

		const { fixString, name, type, parts } = detailedMessage;
		const item = { date: new Date(), message: fixString, name, fixString, type, parts };

		// extra.request allows for replay and edit
		item.extra = { request: true };

		if (this.requestsList) {
			this.requestsList.addItem(item, true);
		}
	}

	addNotification(message) {
		let item = {date: new Date(), message: message};
		if (this.notificationsList) {
			this.notificationsList.addItem(item, true);
		}
	}

	addFixNotification({ fixString, name, type, parts }) {
		const item = { date: new Date(), message: fixString, name, fixString, type, parts };
		if (this.notificationsList) {
			this.notificationsList.addItem(item, true);
		}
	}

	loginFix(clientId, clientSecret) {
		const message = this.fixClient.getLoginMessage(clientId, clientSecret);
		this.sendFixRequest(message);
	}

	handleFixMessage(message) {
		if (this.fixClient.isHeartbeatMessage(message.type)) {
			this.sendFixRequest({ type: message.type }, false);
            return;
		}

		this.addFixNotification(message);
	}

	onFixConnected() {
		this.setState({ fixConnected: true, snackbarOpen: false });
	}

	onFixDisconnected() {
		this.setState({ fixConnected: false });
	}

	onFixError() {
		this.setState({ snackbarOpen: true });
	}

	getNextRequestID() {
		let id = this.state.requestID;
		this.setState({requestID: id + 1});
		return id;
	}

	isConnected() {
		return this.state.wsState;
	}

	isAuthenticated() {
		return this.state.isFix
			? this.state.fixConnected
			: !!this.state.token.access_token;
	}

	accessTokenChanged(accessToken) {
		let token = this.state.token;
		token.access_token = accessToken;
		this.setState({token: token});
	}

	refreshTokenChanged(refreshToken) {
		let token = this.state.token;
		token.refresh_token = refreshToken;
		this.setState({token: token});
	}

	completeRequest(request, forceNoToken=false) {
		request.method = request.method.replace(/^\/+/, '');
		request.jsonrpc = request.jsonrpc || '2.0';
		request.id = request.id || this.getNextRequestID();
		if(request.method.startsWith('private') && this.state.token.access_token !== undefined && !forceNoToken) {
			request.params.access_token = request.params.access_token || this.state.token.access_token;
		}
	}

	// send request ---------------------------------------------------

	sendRequest(request, type, extra) {
		if(type === 'WS') return this.sendWsRequest(request, true, extra);
		else if(type === 'GET' || type === 'POST') return this.sendHttpRequest(request, type, extra);
		else if(type === 'FIX') return this.sendFixRequest(request);
		else console.error('expected type: GET, POST\nactual type: ' + type);
	}

	sendRawFixRequest(fixString) {
		if (!fixString) {
			return;
		}

		const { errors, message } = this.fixClient.getMessageFromRawFix(fixString);
		if (message) {
			this.setState({ rawFixErrors: [] });
			this.sendFixRequest(message);
		} else {
			errors
				? this.setState({ rawFixErrors: errors } )
				: this.setState({ rawFixErrors: [{ message: 'Invalid format' }] });
		}
	}

	sendFixRequest(request, addRequest = true) {
		this.fixClient.send(request, sent => {
            if (addRequest) {
                this.addFixRequest(sent);
            }
        });
	}


	sendWsRequest(request, save = true, extra) {
		if(this.isConnected()) {
			this.completeRequest(request, true);
			this.state.ws.send(JSON.stringify(request));
			switch (request.method) {
			case 'public/auth':
				this.getTokenRequestID = request.id;
				// this.setState({getTokenRequestID: request.id});
				break;
			default:
				break;
			}
			if(save) {
				this.addRequest(request, request.method, 'ws', true, {...extra, request});
				this.wsRequestInfo[request.id] = {method: request.method, extra: {...extra, request}};
			}
		}
	}

	sendHttpRequest(request, type, extra) {
		extra = {...extra, request: {...request}};
		const xhr = new XMLHttpRequest();

		xhr.onreadystatechange = () => {
			if(xhr.readyState === 4) {
				try {
					let response = safeJsonParse(xhr.responseText);
					this.addRequest({headers: xhr.getAllResponseHeaders(), content: response}, request.method, type.toLowerCase(), false, extra);
					if(request.method === 'public/auth' && response.result !== undefined) {
						this.setState({token: response.result});
					}
				} catch (e) {
					console.error(e, xhr);
					// this.addRequest(xhr.getAllResponseHeaders().join('\n'), request.method, type.toLowerCase(), false);
					this.addRequest('error occurred', request.method, type.toLowerCase(), false, extra);
				}
			}
		};

		let url = this.getRestUrl();
		this.completeRequest(request, true);
		if(type === 'GET') {
			url = url + '/' + request.method + '?' + toQsParams(request.params);
		}
		let data = type === 'GET' ? null : JSON.stringify(request);

		xhr.open(type, url);
		if(request.method.startsWith('private') && this.state.token.access_token !== undefined) {
			xhr.setRequestHeader('Authorization', 'Bearer ' + this.state.token.access_token);
		}
		xhr.send(data);

		if(type === 'GET') {
			this.addRequest(url, request.method, type.toLowerCase(), true, extra);
		} else if(type==='POST') {
			this.addRequest(request, request.method, type.toLowerCase(), true, extra);
		}
	}

	getToken(grant_type, paramA, paramB) {
		this.authScope = getScopeString(this.state.serverUrl);
		let params = {
			'grant_type': grant_type,
			'scope': this.authScope,
		};
		if(grant_type === 'client_credentials') {
			params['client_id'] = paramA;
			params['client_secret'] = paramB;
		} else if(grant_type === 'refresh_token') {
			params['refresh_token'] = paramA;
		} else {
			console.error(`invalid grant_type: ${grant_type}`);
			return;
		}
		let request = {
			'id': this.getNextRequestID(),
			'method': 'public/auth',
			'params': params
		};
		this.sendWsRequest(request);
		this.getTokenRequestID = request.id;
	}

	refreshToken() {
		if(this.state.token.refresh_token !== undefined) {
			let request = {
				'id': this.getNextRequestID(),
				'method': 'public/auth',
				'params': {
					'grant_type': 'refresh_token',
					'refresh_token': this.state.token.refresh_token,
					'scope': this.authScope
				}
			};
			this.sendWsRequest(request);
			this.getTokenRequestID = request.id;
		}
	}

	invalidateToken() {
		if(this.state.token.access_token) {
			let request = {
				id: this.getNextRequestID(),
				method: 'private/logout',
				params: { }
			};
			this.sendWsRequest(request);
		}
		this.setState({token: {}});
	}

	unsubAll(showMessage = true) {
		if (this.state.isFix) {
			this.addFixDisconnectRequest();
			this.fixClient.disconnect();
		} else {
			this.sendWsRequest({method: 'public/unsubscribe_all'}, showMessage);
		}
	}

	addFixDisconnectRequest() {
		let item = { date: new Date(), fixDisconnect: true, name: 'Disconnect' };
		if (this.requestsList) {
			this.requestsList.addItem(item, true);
		}
	}

	// layout management

	saveLayout() {
		const layoutKey = this.state.isFix
			? LAYOUT_FIX
			: LAYOUT;

		localStorage.setItem(layoutKey, this.state.model);
	}

	loadLayout() {
		const layoutKey = this.state.isFix
			? LAYOUT_FIX
			: LAYOUT;

		const cachedLayout = localStorage.getItem(layoutKey);

		if(cachedLayout) {
			this.setState({model: FlexLayout.Model.fromJson(JSON.parse(cachedLayout))});
		}
	}

	resetLayout() {
		this.setState({model: FlexLayout.Model.fromJson(this.getDefaultLayout(this.state.isFix))});
	}

	getDefaultLayout(isFix) {
		return isFix
			? defaultLayoutFix
			: defaultLayout;
	}

	// render ---------------------------------------------------------

	requestSummary(item) {
		let date = simpleTime(item.date);
		return date + ' ' + item.type + ' ' + (item.isRequest ? 'request ' : 'response ') + item.method;
	}

	replayRequest(item) {
		if (this.state.isFix) {
			this.sendRawFixRequest(item.fixString);
		} else {
			let type = item.type.toUpperCase();
			let request = {...item.extra.request, id: undefined};
			this.sendRequest(request, type, {...item.extra});
		}

		setTimeout(() => {
			this.requestsList.setState({tailing: true});
		});
	}

	editRequest(item) {
		if(this.requestControl && this.requestControl.requestBuilder) {
			let data = item.extra;
			if(!data || (!data.method && !data.params)) {
				data = {method: item.method, params: JSON.parse(JSON.stringify(item.message.params))};
			}
			this.requestControl.requestBuilder.setRequestObject(data);
		}
	}

	requestRow(item) {
		// return [simpleTime(item.date), item.type, (item.isRequest ? <><ArrowDropUp className="inline-icon red"/>request</> : <><ArrowDropDown className="inline-icon green"/>response</>), item.method];
		if (this.state.isFix) {
			return item.fixDisconnect
				? [simpleTime(item.date), <><ArrowDropUp className="inline-icon red"/></>, item.name]
				: [
					simpleTime(item.date),
					<><ArrowDropUp className="inline-icon red"/></>,
					item.name,
					item.type,
					<><Tooltip title="Repeat request"><Replay className="icon-button" cursor="pointer" onClick={() => this.replayRequest(item)}/></Tooltip></>
				];
		}

		return [
			simpleTime(item.date),
			item.type,
			(item.isRequest ?
				<><ArrowDropUp className="inline-icon red"/> request</> :
				<><ArrowDropDown className="inline-icon green"/> response</>
			),
			item.method,
			item.isRequest ? <Tooltip title="Edit request"><Edit className="icon-button" cursor="pointer" onClick={() => this.editRequest(item)}/></Tooltip> : <></>,
			item.isRequest ? <Tooltip title="Repeat request"><Replay className="icon-button" cursor="pointer" onClick={() => this.replayRequest(item)}/></Tooltip> : <></>,
			allowCopyInDetailsList ? <Tooltip title="Copy to clipboard"><FileCopy className="icon-button" cursor="pointer" onClick={() => copyToClipboard(renderItem(item))}/></Tooltip> : <></>
		];
	}

	notificationSummary(item) {
		let date = simpleTime(item.date);
		return date + ' ' + (item.message.method === 'subscription' ? item.message.params.channel : item.message.method);
	}

	notificationRow(item) {
		if (this.state.isFix) {
			return [simpleTime(item.date), <><ArrowDropDown className="inline-icon green"/></>, item.name, item.type];
		}
		let row = item.message.method === 'subscription' ? item.message.params.channel : item.message.method;
		return [simpleTime(item.date), row];
	}

	factory(node) {
		var component = node.getComponent();
		const fixColumnSizes = [['110px'], ['26px'], ['150px', true], ['50px', true], iconCell, iconCell, iconCell];

		const notificationColumnSizes = this.state.isFix
			? fixColumnSizes
			: [['110px'], ['150px', true]];
		const requestColumnSizes = this.state.isFix
			? fixColumnSizes
			: [['110px'], ['50px'], ['95px'], ['150px', true], iconCell, iconCell, iconCell];

		switch(component) {
		case 'Request':
			return <RequestControl
				sendRequest={this.sendRequest}
				platformUrl={getPlatformHost()}
				serverUrl={this.state.serverUrl}
				service={this.state.selectedService}
				fixApiPath={this.state.fixApiPath}
				isFix={this.state.isFix}
				onServerUrlChange={value => this.setServerUrl(value)}
				defaultServerUrl={defaultServerUrl}
				ref={ref => {this.requestControl = ref;}}
				testnet={!!this.state.testnet}
				authenticated={this.isAuthenticated()}
			/>;
		case 'RequestRaw':
			return this.state.isFix
				? <FixRequestRawInput
					onSend={this.sendRawFixRequest.bind(this)}
					onClear={() => this.setState({ rawFixErrors: [] })}
					errors={this.state.rawFixErrors}
				/>
				: <RequestRawInput onSend={this.sendRequest.bind(this)}/>;
		case 'Token':
			return <TokenControl
				onGetToken={this.getToken}
				onRefreshToken={this.refreshToken}
				onInvalidate={this.invalidateToken}

				onAccessTokenChanged={this.accessTokenChanged}
				onRefreshTokenChanged={this.refreshTokenChanged}

				access_token={this.state.token.access_token}
				refresh_token={this.state.token.refresh_token}
				serverUrl={this.state.serverUrl}

				ref={(ref) => {this.tokenControl = ref; if(ref && this.onTokenControlReady)this.onTokenControlReady(ref);}}
			/>;
		case 'FixLogin':
			return <FixLoginControl
				onSubmit={this.loginFix.bind(this)}
			/>;
		case 'RequestsList':
			return (<DetailsItemList
				ref={ref => {this.requestsList = ref;}}
				getSummary={this.requestRow.bind(this)}
				onItemClick={this.showDetails.bind(this)}
				columnSizes={requestColumnSizes}
			/>);
		case 'NotificationsList':
			return (<DetailsItemList
				ref={ref => {this.notificationsList = ref;}}
				getSummary={this.notificationRow.bind(this)}
				onItemClick={this.showDetails.bind(this)}
				columnSizes={notificationColumnSizes}
				bonus={
					<Button color="primary" onClick={this.unsubAll}>
						{ this.state.isFix ? 'Disconnect ':'Unsubscribe' }
					</Button>
				}
			/>);
		case 'Details':
			return (<DetailsTabs
				ref={ref => {this.details = ref;}}
				replayRequest={this.replayRequest.bind(this)}
				editRequest={this.state.isFix ? undefined : this.editRequest.bind(this)}
				isFix={this.state.isFix}
			/>);
		default:
			return <div/>;
		}
	}

	factoryWrapped(node) {
		return (
			<div style={{height: '100%', overflow: 'hidden'}}>
				{this.factory(node)}
			</div>
		);
	}

	handleSnackbarClose(event, reason) {
		if (reason === 'clickaway') {
			return;
		}
		this.setState({snackbarOpen: false});
	}

	renderSnackbar() {
		return (
			<Snackbar
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				open={this.state.snackbarOpen}
				onClose={this.handleSnackbarClose.bind(this)}
				disableWindowBlurListener

				ContentProps={{
					'aria-describedby': 'message-id',
					style: { background: muiTheme.palette.error.main, color: muiTheme.palette.text.primary },
				}}
				message={
					<div style={{display: 'flex', alignItems: 'center',}}>
						<Warning/>
						Server disconnected.
					</div>
				}
				action={[
					!this.state.isFix &&
					<Button key="retry" onClick={() => { this.openWs(); }}>
						Retry
					</Button>,
					<IconButton
						key="close"
						aria-label="Close"
						color="inherit"
						onClick={this.handleSnackbarClose.bind(this)}
					>
						<Close />
					</IconButton>,
				]}
			/>
		);
	}

	getConnectionStatus() {
		return this.state.isFix
			? this.state.fixConnected
			: this.state.wsState === WS_OPENED;
	}

	getStatusIconTooltipText() {
		return this.state.isFix
			? 'FIX Server connection status - green if logged in, red otherwise.'
			: 'Server connection status - green if connected, red otherwise.';
	}

	onStatusIconClick() {
		if (this.state.isFix) {
			return;
		}

		if (this.state.wsState === WS_CLOSED) {
			this.openWs();
		}
	}

	setSelectedService(service) {
		this.unsubAll(false);
		this.setState({model: FlexLayout.Model.fromJson(this.getDefaultLayout(service === 'fix'))});

		if (service === 'fix') {
			this.setState({ isFix: true, selectedService: undefined });
			this.closeWs();
		} else {
			this.setState({ isFix: false, selectedService: this.state.services[service] });

			const { wsEndpoint } = this.state.services[service];
			if (wsEndpoint) {
				setTimeout(() =>
					this.openWs(wsEndpoint, () => console.log(`switched ws to ${wsEndpoint}`))
				);
			}
		}

	}

	render() {
		return (
			<ThemeProvider theme={muiTheme}>
				<CssBaseline />
				<AppBar color="secondary">
					<img width="96px" alt="logo" style={{ boxSizing: 'initial', display: 'inline-block', verticalAlign: 'top', objectFit: 'contain', paddingRight: 20, paddingLeft: 5}} src={pathTo('deribit-logo-prod.svg')} />
					<Tooltip title={this.getStatusIconTooltipText()}>
						<div style={{display: 'inline-flex', background: this.getConnectionStatus() ? '#7aae1a' : '#ff1111', fontSize: '14px', width: '1em', height: '1em', borderRadius: '0.5em', margin: 'auto 0'}}
							onClick={() => this.onStatusIconClick()}></div>
					</Tooltip>
					<div style={{marginLeft: 'auto', display: 'inline-flex'}}>
						<Popup
							trigger={
								<div style={{padding: '0 4px'}}>
									<div className="layout-button">
										<div>
											<div>{ this.state.isFix ? 'FIX' : this.state.selectedService?.name }<span style={{paddingLeft: 4}}>▼</span></div>
										</div>
									</div>
								</div>
							}
							position="bottom right"
							contentStyle={{
								background: '#313d4e',
								border: '1px solid #313d4e',
								padding: '0',
								width: 160
							}}
							arrow={false}
						>
							{(close) => (<MenuList>
								{Object.keys(this.state.services || {}).map(service =>
									<MenuItem key={service} onClick={() => {this.setSelectedService(service); close();}} selected={this.state.selectedService === this.state.services[service]}>
										{this.state.services[service].name}
									</MenuItem>
								)}
                                {
                                    (this.state.serverUrl.includes('localhost') || this.state.serverUrl.includes('test.deribit.com')) &&
                                    <MenuItem onClick={() => {this.setSelectedService('fix'); close();}} selected={this.state.isFix}>FIX</MenuItem>
                                }
							</MenuList>)}
						</Popup>
						<Popup
							trigger={
								<div style={{padding: '0 4px'}}>
									<div className="layout-button">
										<div>
											<div>Layout<span style={{paddingLeft: 4}}>▼</span></div>
										</div>
									</div>
								</div>
							}
							position="bottom right"
							contentStyle={{
								background: '#313d4e',
								border: '1px solid #313d4e',
								padding: '0',
								width: 160
							}}
							arrow={false}
						>
							{(close) => (<MenuList>
								<MenuItem onClick={() => {this.saveLayout(); close();}}>Save custom layout</MenuItem>
								<MenuItem onClick={() => {this.loadLayout(); close();}}>Load custom layout</MenuItem>
								<MenuItem onClick={() => {this.resetLayout(); close();}}>Restore default layout</MenuItem>
							</MenuList>)}
						</Popup>
					</div>
				</AppBar>
				{ this.state.services?.main &&
					<FlexLayout.Layout model={ this.state.model } factory={ this.factoryWrapped.bind(this) }/>
				}
				{this.renderSnackbar()}
			</ThemeProvider>
		);
	}
}

export default withStyles(muiStyles)(App);
